import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  CheckCircle2,
  FileText,
  AlertCircle,
  Sparkles,
  Scale,
  RefreshCw,
} from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";

export const Route = createFileRoute("/editorial-policy")({
  loader: () => fetchAuthorProfile(),
  head: () => ({
    meta: [
      { title: "Editorial Policy & Standards | BuildWithJIT" },
      {
        name: "description",
        content:
          "Our editorial principles, technical accuracy standards, AI disclosures, benchmarking methodology, and corrections policy at BuildWithJIT.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      { property: "og:title", content: "Editorial Policy & Standards | BuildWithJIT" },
      {
        property: "og:description",
        content:
          "Our editorial principles, technical accuracy standards, AI disclosures, benchmarking methodology, and corrections policy at BuildWithJIT.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://buildwithjit.com/editorial-policy" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "BuildWithJIT Editorial Policy & Standards" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      { name: "twitter:title", content: "Editorial Policy & Standards | BuildWithJIT" },
      {
        name: "twitter:description",
        content:
          "Our editorial principles, technical accuracy standards, AI disclosures, benchmarking methodology, and corrections policy at BuildWithJIT.",
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/editorial-policy" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: EditorialPolicyPage,
});

function EditorialPolicyPage() {
  const profile = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const editorialSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://buildwithjit.com/editorial-policy#webpage",
    name: "Editorial Policy & Standards",
    url: "https://buildwithjit.com/editorial-policy",
    description:
      "Editorial guidelines, technical accuracy standards, and verification principles governing BuildWithJIT.",
    publisher: {
      "@type": "Organization",
      name: "BuildWithJIT",
      url: "https://buildwithjit.com",
      sameAs: Object.values(social),
      logo: {
        "@type": "ImageObject",
        url: "https://buildwithjit.com/icon-512.png",
        width: 512,
        height: 512,
      },
    },
  };

  return (
    <SiteShell social={social}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(editorialSchema) }}
      />
      <div className="container-wide py-12 md:py-20 px-4 sm:px-6 max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8 font-mono text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground font-medium" aria-current="page">
              Editorial Policy
            </li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-12 border-b border-border/80 pb-8">
          <div className="section-kicker font-mono text-xs uppercase tracking-widest text-primary mb-3">
            STANDARDS &amp; INTEGRITY
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Editorial Policy &amp; Standards
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            How BuildWithJIT ensures technical accuracy, reproducibility, transparent benchmarking,
            and ethical authorship across all publications.
          </p>
          <div className="mt-4 flex items-center gap-3 font-mono text-xs text-muted-foreground">
            <span>Last reviewed: October 2026</span>
            <span>•</span>
            <span>Governed by {profile.name}</span>
          </div>
        </header>

        {/* Content Body */}
        <article className="prose dark:prose-invert max-w-none text-muted-foreground space-y-10 text-base leading-relaxed">
          <section className="space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <CheckCircle2 className="text-primary size-6 shrink-0" />
              1. Editorial Mission &amp; Purpose
            </h2>
            <p>
              BuildWithJIT is an independent engineering publication dedicated to modern artificial
              intelligence systems, retrieval-augmented generation (RAG), voice AI agents, and
              workflow automations. Our primary objective is to deliver practical, battle-tested
              architectural guides rather than promotional hype.
            </p>
            <p>
              Every article is written from firsthand software implementation experience by our
              founder,{" "}
              <Link
                to="/author/jignesh-mevada"
                className="text-primary hover:underline font-medium"
              >
                {profile.name}
              </Link>
              , or qualified contributing engineers.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <Scale className="text-primary size-6 shrink-0" />
              2. Technical Verification &amp; Code Quality
            </h2>
            <p>We adhere to strict verification criteria prior to publication:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-foreground">Reproducible Code:</strong> All code snippets,
                CLI commands, and configuration snippets must be executed and validated in a
                production or clean staging environment.
              </li>
              <li>
                <strong className="text-foreground">Objective Benchmarks:</strong> Latency metrics,
                cost estimates, and accuracy scores are measured under documented hardware and
                environmental conditions.
              </li>
              <li>
                <strong className="text-foreground">Explicit Dependencies:</strong> Library
                versions, model checkpoints, and runtime requirements are explicitly stated to
                prevent bit-rot and execution failures.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <Sparkles className="text-primary size-6 shrink-0" />
              3. AI-Assisted Research &amp; Disclosure Policy
            </h2>
            <p>
              While we cover cutting-edge artificial intelligence, our commitment to genuine
              engineering expertise is paramount:
            </p>
            <p>
              AI tooling (e.g., automated syntax formatting, grammatical proofreading, or
              preliminary API reference retrieval) may be utilized during drafting. However, all
              technical conclusions, architecture diagrams, code logic, and opinions are authored,
              reviewed, and endorsed by human engineers. We do not publish automated, unverified
              programmatic content.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <RefreshCw className="text-primary size-6 shrink-0" />
              4. Corrections &amp; Updates Policy
            </h2>
            <p>
              The AI software ecosystem evolves rapidly. When framework APIs change or community
              feedback reveals an inaccuracy:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Articles are revised promptly with a visible "Last updated" signal.</li>
              <li>
                Substantial revisions that alter code compatibility or conclusions include an
                explicit change note.
              </li>
              <li>
                Readers are encouraged to report errata or obsolete code examples via our{" "}
                <Link to="/contact" className="text-primary hover:underline font-medium">
                  Contact Form
                </Link>
                .
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <ShieldCheck className="text-primary size-6 shrink-0" />
              5. Commercial Independence &amp; Sponsorships
            </h2>
            <p>
              BuildWithJIT maintains strict editorial independence. We do not accept paid reviews
              for software tools without explicit sponsorship disclosures. Our evaluations of models
              (e.g., Claude, OpenAI, Gemini, Qwen, DeepSeek) and platforms are based purely on
              architectural merit, cost efficiency, and developer experience.
            </p>
          </section>
        </article>
      </div>
    </SiteShell>
  );
}
